import React, {
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import PropTypes from "kf-proptypes/widgets";
import { BaseDropdown, BASE_DROPDOWN_POSITION } from "@widgets/inputs/dropdown";
import { IconPicker } from "@widgets/inputs/iconpicker";

import { generateId, isEqual } from "base/utils";
import { validateEntireFiles } from "kfplatform/filePicker";

import {
  ICON_SIZE,
  ICON_STROKE,
  KF_ICONS,
  KfIcon
} from "@widgets/common/components/primitives/kf_icon/index.jsx";
import { CHANNEL_ICON_CONFIG, ICON_TYPES } from "./constant.js";

import styles from "./flowicon.css";

export const IconEdit = React.forwardRef(IconEditComponent);

const ICON_PROP_TYPES = PropTypes.oneOfType([
  PropTypes.shape({
    default: PropTypes.shape({
      key: PropTypes.string.isRequired,
      height: PropTypes.number,
      width: PropTypes.number,
      fileExtension: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      size: PropTypes.number.isRequired,
      duration: PropTypes.number
    }).isRequired,
    preview: PropTypes.shape({
      key: PropTypes.string.isRequired,
      height: PropTypes.number,
      width: PropTypes.number,
      fileExtension: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      size: PropTypes.number.isRequired,
      duration: PropTypes.number
    }).isRequired,
    collage: PropTypes.shape({
      key: PropTypes.string.isRequired,
      height: PropTypes.number,
      width: PropTypes.number,
      fileExtension: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      size: PropTypes.number.isRequired,
      duration: PropTypes.number
    }).isRequired,
    thumbnail: PropTypes.shape({
      key: PropTypes.string.isRequired,
      height: PropTypes.number,
      width: PropTypes.number,
      fileExtension: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      size: PropTypes.number.isRequired,
      duration: PropTypes.number
    }).isRequired,
    original: PropTypes.shape({
      key: PropTypes.string.isRequired,
      height: PropTypes.number,
      width: PropTypes.number,
      fileExtension: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      size: PropTypes.number.isRequired,
      duration: PropTypes.number
    }).isRequired
  }),
  PropTypes.shape({
    Color: PropTypes.string,
    Id: PropTypes.string
  }),
  PropTypes.shape({
    Type: PropTypes.string,
    Value: PropTypes.shape({
      Color: PropTypes.string,
      Id: PropTypes.string
    })
  })
]).isRequired;

IconEditComponent.propTypes = {
  onUpdate: PropTypes.func,
  icon: ICON_PROP_TYPES,
  flowType: PropTypes.string,
  flowId: PropTypes.string,
  onChange: PropTypes.func,
  defaultIconId: PropTypes.string,
  isCreate: PropTypes.bool,
  setBlob: PropTypes.func,
  iconRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }).isRequired,
  showEditAlways: PropTypes.bool,
  actualIconValue: ICON_PROP_TYPES
};
export function IconEditComponent(
  {
    onUpdate,
    icon,
    flowType,
    flowId,
    onChange,
    defaultIconId,
    isCreate,
    iconRef,
    showEditAlways,
    actualIconValue = {}
  },
  ref
) {
  const pickerRef = useRef();
  const [currentIcon, setcurrentIcon] = useState(icon?.Id);
  const [currentColor, setcurrentColor] = useState(icon?.Color);
  const [activeEditIcon, setActiveEditIcon] = useState(false);
  const [currentCustomIcon, setcurrentCustomIcon] = useState();
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [blob, setBlob] = useState();
  const fileWrapperRef = useRef();

  useLayoutEffect(function fileUploadEffect() {
    async function importFileUpload() {
      let { FileWrapper } = await import("kfplatform/filePicker");
      fileWrapperRef.current = FileWrapper;
    }

    importFileUpload();
  }, []);

  useEffect(
    function iconListen() {
      setcurrentIcon(icon?.Id);
      setcurrentColor(icon?.Color);
    },
    [icon]
  );

  const openPicker = () => {
    pickerRef.current.click();
  };

  function onChangeIcon(
    { iconValue, colorValue, customValue, isUpdateShape = false },
    update = false
  ) {
    let newIcon = null;
    let iconval = iconValue || currentIcon;
    let colorval = colorValue || currentColor;
    let customval = customValue || currentCustomIcon;
    if (customval && !isUpdateShape) {
      if (customval?.collage?.key || customval?.key) {
        newIcon = { Value: customval, Type: ICON_TYPES.CUSTOM };
      }
    } else if (iconval || colorval) {
      if (colorval && !iconval) {
        newIcon = {
          Type: ICON_TYPES.PRESET,
          Value: { Id: defaultIconId, Color: colorval }
        };
      }
      if (iconval && colorval) {
        newIcon = {
          Type: ICON_TYPES.PRESET,
          Value: { Id: iconval, Color: colorval }
        };
      } else if (iconval && !colorval) {
        newIcon = {
          Type: ICON_TYPES.PRESET,
          Value: { Id: iconval }
        };
      }
    }
    if (update && !isEqual(actualIconValue, newIcon)) {
      onUpdate(newIcon);
    }
    return newIcon;
  }

  const onSelect = ({ color, shape, isReset }) => {
    if (shape) {
      setcurrentIcon(shape);
      setcurrentCustomIcon("");
      setUploadingFiles({});
      let newiconvalue = onChangeIcon({
        iconValue: shape,
        isUpdateShape: true
      });
      onChange({ newIcon: newiconvalue });
    }
    if (color) {
      setcurrentColor(color);
      setcurrentCustomIcon("");
      setUploadingFiles({});
      let newiconvalue = onChangeIcon({
        colorValue: color,
        isUpdateShape: true
      });
      onChange({ newIcon: newiconvalue });
    } else if (isReset) {
      setcurrentIcon("");
      setcurrentColor("");
      setcurrentCustomIcon("");
      setUploadingFiles({});
      onChange({ isReset });
    }
  };

  function updateImages(image) {
    let images = {
      default: {
        key: image.photos[2].key,
        size: image.photos[2].size,
        height: image.photos[2].height,
        width: image.photos[2].width,
        fileExtension: image.photos[2].fileExtension,
        name: image.name
      },
      preview: {
        key: image.photos[1].key,
        size: image.photos[1].size,
        height: image.photos[1].height,
        width: image.photos[1].width,
        fileExtension: image.photos[1].fileExtension,
        name: image.name
      },
      collage: {
        key: image.photos[3].key,
        size: image.photos[3].size,
        height: image.photos[3].height,
        width: image.photos[3].width,
        fileExtension: image.photos[3].fileExtension,
        name: image.name
      },
      thumbnail: {
        key: image.photos[4].key,
        size: image.photos[4].size,
        height: image.photos[4].height,
        width: image.photos[4].width,
        fileExtension: image.photos[4].fileExtension,
        name: image.name
      },
      original: {
        key: image.photos[0].key,
        size: image.photos[0].size,
        height: image.photos[0].height,
        width: image.photos[0].width,
        fileExtension: image.photos[0].fileExtension,
        name: image.name
      }
    };
    setcurrentCustomIcon(images);
    let newiconvalue = onChangeIcon({ customValue: images });
    onChange({ newIcon: newiconvalue });
    return newiconvalue;
  }

  const localFileSelectHandler = (e) => {
    let files = e.target.files || e.dataTransfer.files;
    if (files.length > 0) {
      files[0].id = generateId("Attach");

      let prefix = `${flowType}/${files[0].id}`;
      if (!isCreate) {
        prefix = `${flowType.toLowerCase()}/${flowId}/${files[0].id}`;
      }
      let uploadingFiles = validateEntireFiles({
        files,
        existingFiles: [],
        config: CHANNEL_ICON_CONFIG,
        prefix
      });
      setUploadingFiles({ raw: files, modified: uploadingFiles });
    }
  };

  function onFileRemove() {
    setUploadingFiles({});
    pickerRef.current.value = null;
  }

  function onFileCancel() {
    uploadingFiles.modified && uploadingFiles.modified[0]?.cancelUpload();
    setUploadingFiles({});
    pickerRef.current.value = null;
    return;
  }

  function onFileUploadDone(file) {
    let previewFileDetails = {
      key: URL?.createObjectURL(uploadingFiles.raw[0])
    };
    setcurrentColor("");
    setcurrentIcon("");
    if (!isCreate) {
      updateImages(file);
    } else {
      setBlob(uploadingFiles.raw[0]);
      setTimeout(() => setcurrentCustomIcon(previewFileDetails));
      onChange({
        newIcon: { Type: ICON_TYPES.CUSTOM, Value: previewFileDetails }
      });
    }
    pickerRef.current.value = null;
  }

  useImperativeHandle(iconRef, () => {
    return {
      uploadCustomImage({ flowType, flowId }, cb) {
        let uploadData = new fileWrapperRef.current({
          file: blob,
          config: CHANNEL_ICON_CONFIG,
          prefix: `${flowType}/${flowId}/${blob.id}`
        });
        uploadData.onResponseDone((err, data = {}) => {
          if (err) {
            console.info("Error", err);
          }
          if (data.uploaded === 100) {
            let newicon = updateImages(data);
            cb(newicon);
          }
        });
      },
      getIcon() {
        let newicon = onChangeIcon({});
        if (newicon) {
          return newicon.Type === ICON_TYPES.CUSTOM && currentCustomIcon.key
            ? { Type: newicon.Type }
            : newicon;
        }
        return null;
      }
    };
  });

  return (
    <>
      <BaseDropdown
        position={BASE_DROPDOWN_POSITION.BOTTOM_LEFT}
        targetRenderer={() => {
          return (
            <div
              onClick={() => setActiveEditIcon(true)}
              className={`${styles.editContainer} ${
                showEditAlways || activeEditIcon ? styles.showEditAlways : ""
              } `}
            >
              <KfIcon
                icon={KF_ICONS.EDIT_1}
                size={ICON_SIZE.SIZE_12}
                stroke={ICON_STROKE.BOLD}
                color="--kf-color-gray-100"
              />
            </div>
          );
        }}
        onClose={() => {
          onChangeIcon({}, true);
          setActiveEditIcon(false);
          setUploadingFiles({});
        }}
        listRenderer={() => {
          return (
            <IconPicker
              openPicker={openPicker}
              onSelect={onSelect}
              selected={currentIcon && { shape: currentIcon }}
              selectedColor={currentColor}
              onFileRemove={onFileRemove}
              onFileCancel={onFileCancel}
              uploadingFiles={uploadingFiles.modified}
              onFileUploadDone={onFileUploadDone}
            />
          );
        }}
      />
      <input
        ref={pickerRef}
        multiple={false}
        type="file"
        onChange={(e) => {
          localFileSelectHandler(e);
        }}
        accept="image/png, image/jpeg, image/jpg, image/jpe"
        hidden={true}
        onClick={(e) => e.stopPropagation()}
      />
    </>
  );
}
